/**
 * Hook to get the membership type ID for the current user
 * Uses membership taxonomy name to look up the corresponding term ID
 */

import { useMemo } from 'react';
import type { UseMembershipTypeIdResult } from '../types';
import type { UseDrupalCollectionHook } from './use-notifications-for-membership';

/**
 * Options for membership type ID hook
 */
export interface UseMembershipTypeIdOptions {
  /** Membership taxonomy name (derived from membership label) */
  membershipTaxonomyName?: string | null;
  /** Custom Drupal collection hook implementation */
  useDrupalCollection: UseDrupalCollectionHook;
}

/**
 * Hook to get the membership type ID for the current user
 * Looks up the taxonomy term ID based on the membership name
 */
export function useMembershipTypeId(
  options: UseMembershipTypeIdOptions
): UseMembershipTypeIdResult {
  const { membershipTaxonomyName, useDrupalCollection } = options;

  // Build query parameters for taxonomy term lookup
  const termParams = useMemo((): Record<string, string> | undefined => {
    if (!membershipTaxonomyName) return undefined;

    const params: Record<string, string> = {
      'filter[name]': membershipTaxonomyName,
      'page[limit]': '1',
      'fields[taxonomy_term--membership_type]': 'name',
    };

    return params;
  }, [membershipTaxonomyName]);

  // Fetch the membership type term
  const { items, isLoading } = useDrupalCollection<Record<string, unknown>>(
    'taxonomy_term--membership_type',
    termParams
  );

  // Extract the ID from the first result
  const id = useMemo((): string | null => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const first = items[0] as { id?: string };
    return String(first?.id || '') || null;
  }, [items]);

  return { id, isLoading };
}

